// « PDF du plan » (§ 8.4 de la spécification) : le serveur dessine le plan annoté en vectoriel, à partir de la variante enregistrée
// (jamais une capture de l'écran). Options : variante, format A4 / A3 paysage, calques, comparaison des variantes. Le PDF est SANS
// montant par défaut (prêt à remettre au client ou au maître d'œuvre) ; la version interne avec montants n'est proposée qu'aux
// comptes à qui la Proposition sert les coûts, et le serveur la refuse aux autres (PDF sans montant).
import { useEffect, useState } from 'react';
import { FileDown } from 'lucide-react';
import { useSession } from '../../../lib/session.tsx';
import { Bouton, ChampChoix, Dialogue } from '../../../components/ui.tsx';
import { CALQUES, type Calque } from './CalquesVariante.tsx';
import type { LigneVue } from './logique.ts';

/** Adresse du PDF (lue par le navigateur dans un nouvel onglet : il s'ouvre, s'enregistre ou s'imprime). */
export function urlPdfPlan(dossierId: string, varianteId: string, o: { format: 'A4' | 'A3'; calques: Set<Calque>; comparaison: boolean; montants: boolean }): string {
  const q = new URLSearchParams({ format: o.format });
  if (o.calques.size !== CALQUES.length) q.set('calques', CALQUES.map(([c]) => c).filter((c) => o.calques.has(c)).join(','));
  if (o.comparaison) q.set('comparaison', '1');
  if (o.montants) q.set('montants', '1');
  return `/api/dossiers/${dossierId}/proposition/${varianteId}/plan.pdf?${q.toString()}`;
}

export function BoutonPdfPlan({ dossierId, variantes, varianteId, calques, comparaison = false }: {
  dossierId: string; variantes: LigneVue[]; varianteId: string; calques?: Set<Calque>; comparaison?: boolean;
}) {
  const { peut } = useSession();
  // La version interne n'est proposée que si le serveur sert les coûts à ce compte (coûts « réservés » : marge cachée avec
  // coefficient matériel pour un autre rôle que l'administrateur, ou compte sans droit aux coûts) ; le serveur applique la même règle.
  const couts = peut('devis.couts') && !variantes.some((v) => v.criteres?.cout?.masque);
  const [ouvert, setOuvert] = useState(false);
  const [choix, setChoix] = useState(varianteId);
  const [format, setFormat] = useState<'A4' | 'A3'>('A4');
  const [actifs, setActifs] = useState<Set<Calque>>(new Set(CALQUES.map(([c]) => c)));
  const [avecComparaison, setAvecComparaison] = useState(comparaison);
  const [montants, setMontants] = useState(false);
  // À l'ouverture : la variante affichée et les calques visibles à l'écran.
  useEffect(() => {
    if (!ouvert) return;
    setChoix(variantes.some((v) => v.id === varianteId) ? varianteId : variantes[0]?.id ?? '');
    setActifs(new Set(calques && calques.size ? calques : CALQUES.map(([c]) => c)));
    setAvecComparaison(comparaison);
    setMontants(false);
  }, [ouvert]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!variantes.length) return null;
  const basculer = (c: Calque) => setActifs((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const url = choix && actifs.size ? urlPdfPlan(dossierId, choix, { format, calques: actifs, comparaison: avecComparaison, montants: couts && montants }) : null;

  return (
    <>
      <Bouton icone={<FileDown aria-hidden />} onClick={() => setOuvert(true)} className="prp-bouton-pdf">PDF du plan</Bouton>
      <Dialogue titre="PDF du plan annoté" ouvert={ouvert} fermer={() => setOuvert(false)}
        pied={<>
          <Bouton onClick={() => setOuvert(false)}>Fermer</Bouton>
          {url
            ? <a className="btn btn-principal" href={url} target="_blank" rel="noopener" onClick={() => setOuvert(false)}><FileDown aria-hidden />Ouvrir le PDF</a>
            : <Bouton variante="principal" disabled>Ouvrir le PDF</Bouton>}
        </>}>
        <div className="pile">
          <p className="petit secondaire">Plan à l’échelle avec son fond, légende et cartouche, puis les tableaux (unités par pièce, réseau par tronçon, dérivations, points à traiter).
            Bandeau « Proposé par TRAME — à valider » sur chaque plan.</p>
          <ChampChoix libelle="Variante" value={choix} onChange={(e) => setChoix(e.target.value)}
            options={variantes.map((v) => ({ valeur: v.id, libelle: `${v.rang}. ${v.libelle}${v.origine === 'technicien' ? ' (technicien)' : ''}` }))} />
          <fieldset className="prp-groupe"><legend>Format (paysage)</legend>
            <div className="segment" role="group" aria-label="Format du PDF">
              {(['A4', 'A3'] as const).map((f) => <button key={f} type="button" aria-pressed={format === f} onClick={() => setFormat(f)}>{f}{f === 'A3' ? ' — plus lisible' : ''}</button>)}
            </div>
          </fieldset>
          <fieldset className="prp-groupe"><legend>Calques imprimés</legend>
            <div className="prp-cases">
              {CALQUES.map(([c, lib]) => (
                <label key={c} className="prp-case"><input type="checkbox" checked={actifs.has(c)} onChange={() => basculer(c)} />{lib}</label>
              ))}
            </div>
            {!actifs.size && <p className="tres-petit prp-txt-danger" role="alert">Choisissez au moins un calque.</p>}
          </fieldset>
          <label className="prp-case"><input type="checkbox" checked={avecComparaison} onChange={(e) => setAvecComparaison(e.target.checked)} />
            Ajouter la comparaison des variantes et de la proposition du technicien (critère par critère)</label>
          {couts
            ? <label className="prp-case"><input type="checkbox" checked={montants} onChange={(e) => setMontants(e.target.checked)} />
              Version interne avec le coût estimé (prix d’achat) — à ne pas remettre au client</label>
            : null}
          <p className="tres-petit secondaire">{couts && montants ? 'Version interne : le coût estimé HT est imprimé.' : 'PDF sans aucun montant : prêt à remettre au client ou au maître d’œuvre.'}</p>
        </div>
      </Dialogue>
    </>
  );
}
